import { useCallback, useState } from 'react';

export type UseSectionsOptions<T> = {
    sections: readonly T[];
    initialSection?: T;
};

export type UseSectionsReturnValue<T> = {
    goToNextSection: () => void;
    goToPreviousSection: () => void;
    goToSection: (section: T) => void;
    isCurrentSection: (section: T) => boolean;
};

export function useSections<T extends string>({
    initialSection,
    sections,
}: UseSectionsOptions<T>): UseSectionsReturnValue<T> {
    const [currentIndex, setCurrentIndex] = useState<number>(
        initialSection ? sections.findIndex(section => section === initialSection) : 0
    );

    const goToNextSection = useCallback(() => {
        setCurrentIndex(currentIndex => (currentIndex < sections.length - 1 ? currentIndex + 1 : currentIndex));
    }, [sections.length]);

    const goToPreviousSection = useCallback(() => {
        setCurrentIndex(currentIndex => (currentIndex > 0 ? currentIndex - 1 : currentIndex));
    }, []);

    const goToSection = useCallback(
        (section: T) => {
            setCurrentIndex(sections.findIndex(s => s === section));
        },
        [sections]
    );

    const isCurrentSection = useCallback(
        (section: T): boolean => {
            return sections[currentIndex] === section;
        },
        [currentIndex, sections]
    );

    return { goToNextSection, goToPreviousSection, goToSection, isCurrentSection };
}
